import React, { useEffect, useState } from "react";
import {
  Box,
  Typography,
  Grid,
  Card,
  CardContent,
  CardMedia,
  Button,
  CircularProgress,
  Dialog,
  DialogTitle,
  DialogContent,
  IconButton,
  Container,
  Chip,
  Fade,
  Zoom,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import PlayCircleOutlineIcon from "@mui/icons-material/PlayCircleOutline";
import ImageIcon from "@mui/icons-material/Image";
import VideoLibraryIcon from "@mui/icons-material/VideoLibrary";
import { getSystemExplanations } from "../../../Services/SystemExplanationsApi";
import { API_BASE_URL_images } from "../../../Services/apiConfig";

// helper to build full image url
const getImageUrl = (path) => {
  if (!path) return null;
  if (typeof path !== "string") return null;
  if (path.startsWith("http") || path.startsWith("data:")) return path;
  const base = (API_BASE_URL_images || "").replace(/\/$/, "");
  const p = path.replace(/^\/+/, "");
  return base ? `${base}/${p}` : path;
};

// helper: extract youtube id from common youtube urls
const extractYouTubeId = (url) => {
  if (!url || typeof url !== "string") return null;
  const ytRegex =
    /(?:youtube\.com\/(?:watch\?.*v=|embed\/|v\/)|youtu\.be\/)([A-Za-z0-9_-]{11})/;
  const match = url.match(ytRegex);
  return match ? match[1] : null;
};

// use youtube-nocookie and params to reduce visible YouTube branding
const getVideoEmbedUrl = (url) => {
  const id = extractYouTubeId(url);
  if (id) {
    const params = new URLSearchParams({
      autoplay: "1",
      rel: "0",
      modestbranding: "1",
      playsinline: "1",
      controls: "1",
      enablejsapi: "1",
    });
    return `https://www.youtube-nocookie.com/embed/${id}?${params.toString()}`;
  }
  // fallback: return original url
  return url;
};

const getVideoThumbnail = (video) => {
  // prefer explicit thumbnail
  if (video.thumbnail) return getImageUrl(video.thumbnail);
  // try YouTube thumbnail
  const id = extractYouTubeId(video.url);
  if (id) return `https://img.youtube.com/vi/${id}/hqdefault.jpg`;
  return null;
};

const AboutTheApp = () => {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // new state for video modal
  const [openVideo, setOpenVideo] = useState(false);
  const [activeVideoUrl, setActiveVideoUrl] = useState(null);

  const handleOpenVideo = (video) => {
    const embed = getVideoEmbedUrl(video.url);
    setActiveVideoUrl(embed);
    setOpenVideo(true);
  };

  const handleCloseVideo = () => {
    setOpenVideo(false);
    setActiveVideoUrl(null);
  };

  // new state for image modal
  const [selectedImage, setSelectedImage] = useState(null);
  const [openImageDialog, setOpenImageDialog] = useState(false);

  const handleOpenImage = (imageUrl) => {
    setSelectedImage(imageUrl);
    setOpenImageDialog(true);
  };

  const handleCloseImage = () => {
    setOpenImageDialog(false);
    setSelectedImage(null);
  };

  const fetchExplanations = async () => {
    try {
      setLoading(true);
      const res = await getSystemExplanations();

      // res = { data: { message, data: [...] }, ... } أو res = { message, data: [...] }
      // يجب دعم كلا الشكلين:
      const dataArray = res?.data?.data ?? res?.data ?? [];

      // لو كانت المصفوفة ليست مصفوفة، اجعلها فارغة
      const finalArray = Array.isArray(dataArray) ? dataArray : [];

      setItems(finalArray);
      console.log("System explanations count:", finalArray.length);
    } catch (err) {
      console.error("Error fetching system explanations:", err);
      setError(err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchExplanations();
  }, []);

  if (loading) {
    return (
      <Box
        sx={{
          py: 10,
          textAlign: "center",
          background: "linear-gradient(135deg, #f5f7fa 0%, #e8eef5 100%)",
          minHeight: "60vh",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
        }}
      >
        <Box>
          <CircularProgress
            size={60}
            thickness={4}
            sx={{
              color: "#f36f21",
              "& .MuiCircularProgress-circle": {
                strokeLinecap: "round",
              },
            }}
          />
          <Typography
            sx={{ mt: 3, color: "text.secondary", fontWeight: 500 }}
          >
            جاري تحميل معلومات التطبيق...
          </Typography>
        </Box>
      </Box>
    );
  }

  if (error) {
    return (
      <Box
        sx={{
          py: 10,
          textAlign: "center",
          background: "linear-gradient(135deg, #f5f7fa 0%, #e8eef5 100%)",
          minHeight: "60vh",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
        }}
      >
        <Box>
          <Typography color="error" variant="h5" fontWeight={700} gutterBottom>
            ⚠️ حدث خطأ أثناء جلب المعلومات
          </Typography>
          <Button
            variant="contained"
            onClick={fetchExplanations}
            sx={{
              mt: 2,
              background: "linear-gradient(135deg, #f36f21 0%, #e65100 100%)",
              "&:hover": {
                background: "linear-gradient(135deg, #e65100 0%, #d84315 100%)",
              },
            }}
          >
            إعادة المحاولة
          </Button>
        </Box>
      </Box>
    );
  }

  return (
    <Box
      sx={{
        py: 10,
        background: "linear-gradient(135deg, #f5f7fa 0%, #e8eef5 100%)",
        position: "relative",
        overflow: "hidden",
        "&::before": {
          content: '""',
          position: "absolute",
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          backgroundImage:
            "radial-gradient(circle, rgba(243, 111, 33, 0.03) 1px, transparent 1px)",
          backgroundSize: "30px 30px",
          pointerEvents: "none",
        },
        "&::after": {
          content: '""',
          position: "absolute",
          top: "10%",
          right: "-5%",
          width: 300,
          height: 300,
          borderRadius: "50%",
          background:
            "radial-gradient(circle, rgba(243, 111, 33, 0.05), transparent 70%)",
          filter: "blur(60px)",
          pointerEvents: "none",
        },
      }}
    >
      <Container maxWidth="lg" sx={{ position: "relative", zIndex: 1 }}>
        <Fade in timeout={800}>
          <Box sx={{ textAlign: "center", mb: 8 }}>
            <Chip
              label="📱 اكتشف المزيد عن النظام"
              sx={{
                mb: 3,
                background:
                  "linear-gradient(135deg, rgba(243, 111, 33, 0.12) 0%, rgba(230, 81, 0, 0.12) 100%)",
                color: "#f36f21",
                fontWeight: 700,
                fontSize: "1rem",
                px: 3,
                py: 3,
                height: "auto",
                border: "2px solid rgba(243, 111, 33, 0.3)",
                boxShadow: "0 4px 12px rgba(243, 111, 33, 0.1)",
                "&:hover": {
                  transform: "translateY(-2px)",
                  boxShadow: "0 6px 16px rgba(243, 111, 33, 0.15)",
                },
              }}
            />
            <Typography
              variant="h3"
              gutterBottom
              fontWeight={900}
              sx={{
                background:
                  "linear-gradient(135deg, #f36f21 0%, #e65100 100%)",
                WebkitBackgroundClip: "text",
                WebkitTextFillColor: "transparent",
                backgroundClip: "text",
                mb: 2,
                fontSize: { xs: "2rem", md: "3rem" },
                letterSpacing: "-0.5px",
              }}
            >
              معلومات شاملة عن التطبيق
            </Typography>
            <Typography
              variant="h6"
              sx={{
                color: "text.secondary",
                maxWidth: 700,
                mx: "auto",
                lineHeight: 1.8,
                fontSize: { xs: "1rem", md: "1.25rem" },
              }}
            >
              تعرف على مميزات وإمكانيات نظام ريستو كاش من خلال الشروحات
              المفصلة والمرئية
            </Typography>
          </Box>
        </Fade>

        {items.length === 0 ? (
          <Box sx={{ textAlign: "center", py: 8 }}>
            <Typography variant="h6" color="text.secondary">
              لا توجد معلومات متاحة حالياً
            </Typography>
          </Box>
        ) : (
          <Grid container spacing={4}>
            {items.map((item, index) => (
              <Grid item xs={12} md={6} key={item.id}>
                <Zoom in timeout={500 + index * 100}>
                  <Card
                    sx={{
                      height: "100%",
                      borderRadius: 5,
                      overflow: "hidden",
                      border: "2px solid #e8e8e8",
                      transition: "all 0.4s cubic-bezier(0.4, 0, 0.2, 1)",
                      background: "white",
                      boxShadow: "0 4px 20px rgba(0,0,0,0.08)",
                      "&:hover": {
                        transform: "translateY(-12px)",
                        boxShadow: "0 20px 50px rgba(243, 111, 33, 0.2)",
                        borderColor: "#f36f21",
                      },
                    }}
                  >
                    {item.image && (
                      <Box
                        sx={{
                          position: "relative",
                          overflow: "hidden",
                          "&::before": {
                            content: '""',
                            position: "absolute",
                            inset: 0,
                            background:
                              "linear-gradient(to bottom, transparent 0%, rgba(0,0,0,0.1) 100%)",
                            zIndex: 1,
                            pointerEvents: "none",
                          },
                        }}
                      >
                        <CardMedia
                          component="img"
                          height="260"
                          image={getImageUrl(item.image)}
                          alt={item.title || "image"}
                          sx={{
                            transition: "transform 0.5s ease",
                            "&:hover": {
                              transform: "scale(1.08)",
                            },
                          }}
                        />
                        <Box
                          sx={{
                            position: "absolute",
                            top: 20,
                            right: 20,
                            background:
                              "linear-gradient(135deg, #f36f21 0%, #e65100 100%)",
                            color: "white",
                            px: 3,
                            py: 1,
                            borderRadius: 3,
                            fontSize: "0.9rem",
                            fontWeight: 700,
                            boxShadow: "0 6px 16px rgba(243, 111, 33, 0.4)",
                            zIndex: 2,
                            backdropFilter: "blur(10px)",
                          }}
                        >
                          {item.type === "info" ? "📚 معلومات" : "🎓 شرح"}
                        </Box>
                      </Box>
                    )}
                    <CardContent sx={{ p: 5 }}>
                      <Typography
                        variant="h5"
                        fontWeight={800}
                        gutterBottom
                        sx={{
                          color: "text.primary",
                          mb: 2,
                          lineHeight: 1.4,
                        }}
                      >
                        {item.title}
                      </Typography>
                      <Typography
                        variant="body1"
                        sx={{
                          color: "text.secondary",
                          mb: 4,
                          lineHeight: 1.9,
                          fontSize: "1.05rem",
                        }}
                      >
                        {item.description}
                      </Typography>

                      {/* صور الشرح */}
                      {item.explanation_images?.length > 0 && (
                        <Box sx={{ mb: 4 }}>
                          <Box
                            sx={{
                              display: "flex",
                              alignItems: "center",
                              gap: 1.5,
                              mb: 2.5,
                            }}
                          >
                            <Box
                              sx={{
                                width: 40,
                                height: 40,
                                borderRadius: 2,
                                background:
                                  "linear-gradient(135deg, rgba(243, 111, 33, 0.1) 0%, rgba(230, 81, 0, 0.1) 100%)",
                                display: "flex",
                                alignItems: "center",
                                justifyContent: "center",
                              }}
                            >
                              <ImageIcon sx={{ color: "#f36f21", fontSize: 22 }} />
                            </Box>
                            <Typography
                              variant="subtitle1"
                              fontWeight={700}
                              sx={{ color: "#f36f21" }}
                            >
                              صور توضيحية ({item.explanation_images.length})
                            </Typography>
                          </Box>
                          <Grid container spacing={2}>
                            {item.explanation_images.map((img) => (
                              <Grid item xs={4} key={img.id}>
                                <Box
                                  component="img"
                                  src={getImageUrl(img.image)}
                                  alt={img.title || "explanation image"}
                                  onClick={() => handleOpenImage(getImageUrl(img.image))}
                                  sx={{
                                    width: "100%",
                                    height: 110,
                                    objectFit: "cover",
                                    borderRadius: 3,
                                    border: "3px solid #f5f5f5",
                                    transition: "all 0.3s ease",
                                    cursor: "pointer",
                                    "&:hover": {
                                      transform: "scale(1.08) rotate(2deg)",
                                      borderColor: "#f36f21",
                                      boxShadow:
                                        "0 8px 20px rgba(243, 111, 33, 0.3)",
                                    },
                                  }}
                                />
                              </Grid>
                            ))}
                          </Grid>
                        </Box>
                      )}

                      {/* فيديوهات الشرح */}
                      {item.explanation_videos?.length > 0 && (
                        <Box>
                          <Box
                            sx={{
                              display: "flex",
                              alignItems: "center",
                              gap: 1.5,
                              mb: 2.5,
                            }}
                          >
                            <Box
                              sx={{
                                width: 40,
                                height: 40,
                                borderRadius: 2,
                                background:
                                  "linear-gradient(135deg, rgba(243, 111, 33, 0.1) 0%, rgba(230, 81, 0, 0.1) 100%)",
                                display: "flex",
                                alignItems: "center",
                                justifyContent: "center",
                              }}
                            >
                              <VideoLibraryIcon
                                sx={{ color: "#f36f21", fontSize: 22 }}
                              />
                            </Box>
                            <Typography
                              variant="subtitle1"
                              fontWeight={700}
                              sx={{ color: "#f36f21" }}
                            >
                              فيديوهات الشرح ({item.explanation_videos.length})
                            </Typography>
                          </Box>
                          <Grid container spacing={2}>
                            {item.explanation_videos.map((v) => (
                              <Grid item xs={12} sm={6} key={v.id}>
                                <Box
                                  onClick={() => handleOpenVideo(v)}
                                  sx={{
                                    cursor: "pointer",
                                    borderRadius: 4,
                                    overflow: "hidden",
                                    position: "relative",
                                    background: "#000",
                                    height: 0,
                                    pt: "56.25%",
                                    border: "3px solid #f0f0f0",
                                    transition: "all 0.4s ease",
                                    "&:hover": {
                                      borderColor: "#f36f21",
                                      transform: "scale(1.03)",
                                      boxShadow:
                                        "0 12px 30px rgba(243, 111, 33, 0.25)",
                                    },
                                  }}
                                >
                                  <Box
                                    component="img"
                                    src={
                                      getVideoThumbnail(v) ||
                                      "/placeholder-video.png"
                                    }
                                    alt={v.title || "video thumbnail"}
                                    sx={{
                                      position: "absolute",
                                      inset: 0,
                                      width: "100%",
                                      height: "100%",
                                      objectFit: "cover",
                                    }}
                                  />
                                  <Box
                                    sx={{
                                      position: "absolute",
                                      inset: 0,
                                      display: "flex",
                                      alignItems: "center",
                                      justifyContent: "center",
                                      background: "rgba(0,0,0,0.35)",
                                      transition: "all 0.3s ease",
                                      "&:hover": {
                                        background: "rgba(0,0,0,0.55)",
                                      },
                                    }}
                                  >
                                    <Box
                                      sx={{
                                        width: 70,
                                        height: 70,
                                        borderRadius: "50%",
                                        background:
                                          "linear-gradient(135deg, #f36f21 0%, #e65100 100%)",
                                        display: "flex",
                                        alignItems: "center",
                                        justifyContent: "center",
                                        boxShadow:
                                          "0 6px 24px rgba(243, 111, 33, 0.6)",
                                        transition: "all 0.3s ease",
                                        "&:hover": {
                                          transform: "scale(1.15)",
                                          boxShadow:
                                            "0 8px 32px rgba(243, 111, 33, 0.8)",
                                        },
                                      }}
                                    >
                                      <PlayCircleOutlineIcon
                                        sx={{ color: "white", fontSize: 40 }}
                                      />
                                    </Box>
                                  </Box>
                                  <Box
                                    sx={{
                                      position: "absolute",
                                      bottom: 0,
                                      left: 0,
                                      right: 0,
                                      background:
                                        "linear-gradient(to top, rgba(0,0,0,0.9) 0%, rgba(0,0,0,0.4) 50%, transparent 100%)",
                                      p: 2.5,
                                    }}
                                  >
                                    <Typography
                                      variant="body2"
                                      sx={{
                                        color: "#fff",
                                        fontWeight: 700,
                                        textShadow:
                                          "0 2px 8px rgba(0,0,0,0.9)",
                                        fontSize: "0.95rem",
                                      }}
                                    >
                                      {v.title}
                                    </Typography>
                                  </Box>
                                </Box>
                              </Grid>
                            ))}
                          </Grid>
                        </Box>
                      )}
                    </CardContent>
                  </Card>
                </Zoom>
              </Grid>
            ))}
          </Grid>
        )}
      </Container>

      {/* Video Dialog */}
      <Dialog
        open={openVideo}
        onClose={handleCloseVideo}
        maxWidth="lg"
        fullWidth
        TransitionComponent={Zoom}
        PaperProps={{
          sx: {
            borderRadius: 5,
            overflow: "hidden",
            boxShadow: "0 20px 60px rgba(0,0,0,0.3)",
          },
        }}
      >
        <DialogTitle
          sx={{
            m: 0,
            p: 3.5,
            background:
              "linear-gradient(135deg, #f36f21 0%, #e65100 100%)",
            color: "white",
            display: "flex",
            alignItems: "center",
            gap: 2,
          }}
        >
          <VideoLibraryIcon sx={{ fontSize: 28 }} />
          <Typography variant="h6" fontWeight={700}>
            مشاهدة الفيديو
          </Typography>
          <IconButton
            aria-label="close"
            onClick={handleCloseVideo}
            sx={{
              position: "absolute",
              right: 16,
              top: 16,
              color: "white",
              background: "rgba(0,0,0,0.25)",
              "&:hover": {
                background: "rgba(0,0,0,0.4)",
                transform: "rotate(90deg)",
              },
            }}
          >
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        <DialogContent
          dividers
          sx={{
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            p: 0,
            bgcolor: "black",
            minHeight: 450,
          }}
        >
          {activeVideoUrl ? (
            <Box
              component="iframe"
              src={activeVideoUrl}
              width="100%"
              height={Math.min(650, Math.round((9 / 16) * window.innerWidth))}
              sx={{ border: 0 }}
              allow="autoplay; encrypted-media; picture-in-picture"
              allowFullScreen
            />
          ) : (
            <Typography sx={{ color: "#fff", p: 3, fontSize: "1.1rem" }}>
              لا يوجد فيديو للعرض
            </Typography>
          )}
        </DialogContent>
      </Dialog>

      {/* Image Dialog */}
      <Dialog
        open={openImageDialog}
        onClose={handleCloseImage}
        maxWidth="lg"
        fullWidth
        TransitionComponent={Zoom}
        PaperProps={{
          sx: {
            borderRadius: 5,
            overflow: "hidden",
            background: "transparent",
            boxShadow: "none",
          },
        }}
      >
        <Box
          sx={{
            position: "relative",
            bgcolor: "rgba(0,0,0,0.9)",
            backdropFilter: "blur(10px)",
          }}
        >
          <IconButton
            aria-label="close"
            onClick={handleCloseImage}
            sx={{
              position: "absolute",
              right: 16,
              top: 16,
              color: "white",
              background: "rgba(243, 111, 33, 0.9)",
              zIndex: 10,
              "&:hover": {
                background: "#f36f21",
                transform: "rotate(90deg)",
              },
            }}
          >
            <CloseIcon />
          </IconButton>
          {selectedImage && (
            <Box
              component="img"
              src={selectedImage}
              alt="Full size"
              sx={{
                width: "100%",
                maxHeight: "90vh",
                objectFit: "contain",
                display: "block",
              }}
            />
          )}
        </Box>
      </Dialog>
    </Box>
  );
};

export default AboutTheApp;
